import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ListCard } from '@/shared/components/list/list-card';
import { useTranslation } from '@/shared/i18n/use-translation';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button/button';
import { Skeleton } from '@/shared/ui/skeleton/skeleton';

type DashboardSectionProps = {
  readonly title: string;
  readonly to: string;
  readonly loading: boolean;
  readonly count: number;
  readonly className?: string;
  readonly children: ReactNode;
};

/** A titled preview of a list; hidden when the list is empty. */
export function DashboardSection({ title, to, loading, count, className, children }: DashboardSectionProps) {
  const { t } = useTranslation();
  if (!loading && count === 0) return null;
  return (
    <section className={cn('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex items-center justify-between">
        <h2 className="font-medium">
          {title}
          {count > 0 && <span className="ml-2 text-sm text-muted-foreground tabular-nums">{count}</span>}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          className="text-foreground"
          nativeButton={false}
          render={<Link to={to} />}
        >
          {t('See all')} <ArrowRight />
        </Button>
      </div>
      {loading ? <Skeleton className="h-32 rounded-xl" /> : <ListCard>{children}</ListCard>}
    </section>
  );
}
